import React, { useEffect, useState } from 'react';
import { X, Copy, Share2, Gift } from 'lucide-react';
import { User } from '../App';
import { toast } from 'sonner';

interface ShareModalProps {
  // 보상 메뉴(Rewards.tsx)는 {showShareModal && <ShareModal .../>} 형태로 조건부 렌더링을
  // 하면서 isOpen을 안 넘겨줬다 -> undefined -> 모달이 절대 안 뜨는 버그가 있었다.
  // 넘어오지 않으면 "열린 것"으로 간주한다.
  isOpen?: boolean;
  onClose: () => void;
  user: User;
}

// [2026-09-11]
// 1) 예전 코드는 다운로드 링크가 'https://wallet.handy.app/' 으로 박혀 있었는데 이 도메인은
//    아예 존재하지 않는다(NXDOMAIN). 카톡으로 공유된 링크가 전부 죽은 링크였다.
//    실제 서비스 주소는 wallet.handylabs.ai 다.
// 2) 지갑 주소는 공유 대상이 아니라서(친구 초대용) 화면·공유문구 양쪽에서 제거했다.
// 3) 화면과 공유문구를 전부 한글로 통일했다(영어 섞여서 못 알아본다는 피드백).
const DOWNLOAD_LINK = 'https://wallet.handylabs.ai/';
const DEFAULT_SIGNUP_BONUS = 6000;

export function ShareModal({ isOpen = true, onClose, user }: ShareModalProps) {
  // 에어드롭 수량은 어드민에서 바꿀 수 있으므로 공개 API에서 읽어온다.
  // 실패하면 기본값 6,000을 그대로 쓴다(공유 자체가 막히면 안 되므로).
  const [signupBonus, setSignupBonus] = useState<number>(DEFAULT_SIGNUP_BONUS);

  useEffect(() => {
    if (!isOpen) return;
    fetch('/api/referrals/reward-policy')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        // 실제 응답: { success: true, policy: { signup_bonus_amount: "6000", ... } }
        const v = Number(d?.policy?.signup_bonus_amount);
        if (Number.isFinite(v) && v > 0) setSignupBonus(v);
      })
      .catch(() => {});
  }, [isOpen]);

  if (!isOpen) return null;

  const referralCode = user?.referralCode || '';
  const bonusText = signupBonus.toLocaleString();

  const copyToClipboard = async (text: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(message);
    } catch (error) {
      // 구형 브라우저 / 클립보드 권한 거부 시 폴백
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(message);
    }
  };

  // 카카오톡 공유는 안드로이드 크롬의 시스템 공유시트(navigator.share)를 통해 나간다.
  // 별도 카카오 SDK 키 없이 동작하고, 공유시트에 카카오톡이 그대로 뜬다.
  // [2026-09-12 긴급]
  // 카카오톡에서 링크를 바로 누르면 '카톡 인앱 브라우저'가 열리는데,
  // 인앱 브라우저는 홈화면 앱 설치(PWA)를 지원하지 않아 설치 버튼이 항상 실패한다.
  // 사용자는 이걸 앱 오류로 오해한다. 그래서 공유 문구에 설치 방법을 반드시 넣는다.
  const shareText =
    `🚀 핸디 월렛(Handy Wallet) 초대합니다!\n\n` +
    `🎁 회원가입만 해도 ${bonusText} wHANDY 에어드롭 지급!\n` +
    `   (아래 추천코드를 입력하면 바로 사용할 수 있어요)\n\n` +
    `📱 추천코드 : ${referralCode}\n\n` +
    `🔗 앱 주소\n${DOWNLOAD_LINK}\n\n` +
    `━━━━━━━━━━━━━━\n` +
    `⚠️ 카카오톡에서는 앱 설치가 안 됩니다\n` +
    `아래 3단계로 설치해주세요\n\n` +
    `1. 위 앱 주소를 길게 눌러 복사\n` +
    `2. 크롬(Chrome) 또는 사파리 열기\n` +
    `3. 주소창에 붙여넣고 접속 후\n` +
    `   화면 하단 "설치하기" 누르기`;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: '핸디 월렛 초대',
          text: shareText,
        });
        return;
      } catch (error: any) {
        // 사용자가 공유시트를 그냥 닫은 경우엔 아무것도 하지 않는다.
        if (error?.name === 'AbortError') return;
      }
    }
    copyToClipboard(shareText, '초대 문구가 복사되었습니다. 카카오톡에 붙여넣기 하세요.');
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-card rounded-3xl p-6 w-full max-w-sm shadow-2xl border border-border">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-foreground">친구 초대하기</h3>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-xl hover:bg-muted"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* 추천코드 */}
          <div className="rounded-2xl p-4" style={{ background: '#FFF4E8' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: '#8A8A8A' }}>내 추천코드</span>
              <button
                onClick={() => copyToClipboard(referralCode, '추천코드가 복사되었습니다.')}
                className="p-1"
                style={{ color: '#FF6B00' }}
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <div className="text-2xl font-bold tracking-wider" style={{ color: '#FF6B00' }}>
              {referralCode}
            </div>
          </div>

          {/* 앱 다운로드 주소 */}
          <div className="rounded-2xl p-4" style={{ background: '#E9F8EF' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: '#8A8A8A' }}>앱 다운로드 주소</span>
              <button
                onClick={() => copyToClipboard(DOWNLOAD_LINK, '다운로드 주소가 복사되었습니다.')}
                className="p-1"
                style={{ color: '#10A45A' }}
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <div className="text-sm font-medium break-all" style={{ color: '#10A45A' }}>
              {DOWNLOAD_LINK}
            </div>
          </div>

          {/* 회원가입 에어드롭 안내 */}
          <div className="rounded-2xl p-4 flex items-start gap-3" style={{ background: '#FFF9E6' }}>
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
              style={{ background: '#FFB020' }}
            >
              <Gift className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-foreground">
                회원가입 시 {bonusText} wHANDY 에어드롭
              </p>
              <p className="text-xs mt-0.5" style={{ color: '#8A8A8A' }}>
                내 추천코드를 입력하고 인증하면 바로 사용할 수 있어요.
              </p>
            </div>
          </div>

          {/* [2026-09-12 긴급] 카톡 인앱 브라우저에서는 PWA 설치가 안 된다.
              공유하는 사람이 먼저 알고 친구에게 알려줄 수 있도록 팝업에도 명시한다. */}
          <div className="rounded-2xl p-4" style={{ background: '#FFF1F0', border: '1px solid #FFCDC9' }}>
            <p className="text-sm font-bold" style={{ color: '#C62828' }}>
              ⚠️ 친구에게 꼭 알려주세요
            </p>
            <p className="text-xs mt-1" style={{ color: '#7F1D1D' }}>
              카카오톡에서는 앱 설치가 되지 않습니다. 아래 순서를 안내해주세요.
            </p>
            <ol className="mt-2 space-y-1.5">
              {[
                '앱 주소 복사하기',
                '크롬·사파리 브라우저 열기',
                '주소 붙여넣고 접속 → “설치하기”',
              ].map((step, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span
                    className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0"
                    style={{ background: '#C62828', color: '#FFFFFF' }}
                  >
                    {i + 1}
                  </span>
                  <span className="text-xs" style={{ color: '#7F1D1D' }}>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* 공유 버튼 */}
          <button
            onClick={handleShare}
            className="w-full py-4 rounded-2xl font-bold text-white flex items-center justify-center gap-3 shadow-lg"
            style={{ background: '#FF6B00' }}
          >
            <Share2 className="w-5 h-5" />
            카카오톡으로 공유하기
          </button>
          <p className="text-[11px] text-center" style={{ color: '#9CA3AF' }}>
            공유 목록에서 카카오톡을 선택하세요
          </p>
        </div>
      </div>
    </div>
  );
}
